/**
 * Plugin Page Host Component
 *
 * Renders plugin-provided pages using dynamic routing.
 * Provides full-height layout integration with the A9.13 layout system.
 *
 * @wave A9.19 — upgraded loading/error/not-found to use shared state pattern components
 * @wave A9.25 — added PluginRuntimeBoundary for async error capture + telemetry
 * @wave 11AU.2 — strict capability enforcement: no EMPTY_CAPS fallback
 */

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePageRegistry } from '../registries';
import type { PluginPageDefinition } from '../registries';
import { PluginIframe } from '../PluginIframe';
import { PageActionProvider, PageActionLayout } from '../../components/actions';
import { useAuthStore } from '../../stores/authStore';
import { PluginUIProvider } from '@neuro/plugin-ui';
import type { ResolvedCapabilities, PluginUIContext as SdkUIContext } from '@neuro/plugin-sdk';
import {
  buildPluginHostContext,
  getPluginEventsApi,
  getScopedPluginApi,
  resolvePluginCapabilities,
} from '../runtime/plugin-runtime-context';
import { usePermissions } from '../registries/permission-registry';
import { reloadForChunkLoadError } from '../../utils/chunkLoadRecovery';

/**
 * Loading fallback component — uses shared LoadingState CSS classes
 */
function PageLoadingFallback() {
  return (
    <div className="nc-loading-state" role="status" aria-label="Loading page">
      <div className="nc-spinner" aria-hidden="true" style={{ width: 48, height: 48 }} />
      <span className="nc-loading-state__text">Loading page…</span>
    </div>
  );
}

/**
 * Error fallback component — uses design tokens
/**
 * Error fallback component — uses shared ErrorState CSS classes
 * @wave A9.25 — added retry button and back navigation
 */
function PageErrorFallback({
  error,
  page,
  onRetry,
}: {
  error: Error;
  page: PluginPageDefinition;
  onRetry?: () => void;
}) {
  return (
    <div className="nc-error-state" role="alert">
      <div className="nc-error-state__icon" aria-hidden="true">⚠️</div>
      <h2 className="nc-error-state__title">Failed to load page</h2>
      <p className="nc-error-state__message">
        The page &ldquo;{page.title}&rdquo; from plugin &ldquo;{page.pluginId}&rdquo; could not be loaded.
      </p>
      <code className="nc-error-state__code">{error.message}</code>
      <div style={{ display: 'flex', gap: 'var(--nc-space-3, 12px)', marginTop: 'var(--nc-space-4, 16px)', justifyContent: 'center' }}>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="nc-btn nc-btn--primary nc-btn--sm"
          >
            Retry
          </button>
        )}
        <button
          type="button"
          onClick={() => window.history.back()}
          className="nc-btn nc-btn--secondary nc-btn--sm"
        >
          Go back
        </button>
      </div>
    </div>
  );
}

/**
 * Not found component — uses shared EmptyState CSS classes
 */
function PageNotFound({ path }: { path: string }) {
  return (
    <div className="nc-empty-state">
      <div className="nc-empty-state__icon" aria-hidden="true">🔍</div>
      <h2 className="nc-empty-state__title">Page not found</h2>
      <p className="nc-empty-state__description">
        No plugin has registered a page for path:{' '}
        <code className="nc-error-state__code">{path}</code>
      </p>
    </div>
  );
}

function PageAccessDenied({ title }: { title: string }) {
  return (
    <div className="nc-error-state" role="alert">
      <div className="nc-error-state__icon" aria-hidden="true">🔒</div>
      <h2 className="nc-error-state__title">Access denied</h2>
      <p className="nc-error-state__message">
        You do not have permission to view &ldquo;{title}&rdquo;.
      </p>
    </div>
  );
}

/**
 * Props for PluginPageHost
 */
export interface PluginPageHostProps {
  /** Optional specific page ID to render */
  pageId?: string;
  /** Optional fallback content if page not found */
  fallback?: React.ReactNode;
}

/**
 * Plugin Page Host Component
 */
export function PluginPageHost({ pageId, fallback }: PluginPageHostProps) {
  const location = useLocation();
  const rawNavigate = useNavigate();
  const [error, setError] = useState<Error | null>(null);
  const { checkPermissions, isLoading: permissionsLoading } = usePermissions();

  // ── Stabilize navigate reference ─────────────────────────────────
  // useNavigate() may return a new function reference on every render
  // (depends on React Router version and router context updates).
  // Wrap it in a ref-backed callback so downstream memos (hostContext,
  // PluginUIProvider runtime) receive a STABLE function identity and
  // don't cause cascading re-renders / PluginPage effect re-runs.
  const navigateRef = useRef(rawNavigate);
  navigateRef.current = rawNavigate;
  const navigate = useCallback((path: string) => {
    return navigateRef.current(path);
  }, []);
  
  // Get page definition
  const page = usePageRegistry((state) => {
    if (pageId) {
      return state.getPage(pageId);
    }
    return state.getPageByPath(location.pathname);
  });

  // ── DIAGNOSTIC: PluginPageHost Routing (debug only) ──────────
  // Moved behind debug flag to avoid triggering store reads on every render
  // which caused unnecessary re-renders and timing issues with plugin hooks.
  const pluginPageHostDebug =
    typeof localStorage !== 'undefined' && localStorage.getItem('pluginPageHost.debug') === '1';
  if (pluginPageHostDebug) {
    const allPages = usePageRegistry.getState().pages;
    const allPaths = Array.from(allPages.values()).map(p => p.path);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('[PluginPageHost] Current pathname:', location.pathname);
    console.log('[PluginPageHost] All registered page paths:', allPaths);
    console.log('[PluginPageHost] Page match result:', page ? `FOUND: ${page.id}` : '❌ NO MATCH');
    if (!page) {
      console.log('[PluginPageHost] pathToPage map:', Object.fromEntries(usePageRegistry.getState().pathToPage));
      console.log('[PluginPageHost] All page details:', Array.from(allPages.values()).map(p => ({ id: p.id, path: p.path, pluginId: p.pluginId })));
    }
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }
  
  // Reset error when page changes
  useEffect(() => {
    setError(null);
  }, [page?.id, location.pathname]);

  // Create API for plugins — returns raw data (not wrapped in { data })
  // so plugin API adapters like createNotesApi work correctly.
  //
  // IMPORTANT: This MUST be memoized. Without useMemo, a new object is
  // created on every render of PluginPageHost, which causes the page
  // component's useMemo(createXxxApi(apiClient), [apiClient]) to recreate
  // the API instance on every render. Under React 18 StrictMode, this
  // creates a race condition where async fetches resolve against stale
  // hook instances, causing data to be silently dropped.
  //
  // NOTE: This hook is intentionally placed BEFORE any early returns to
  // comply with the React Rules of Hooks (hooks must not be called
  // conditionally or after early returns).
  //
  // URL routing: The proxy accepts both full paths (e.g. /api/plugins/audio/…)
  // and plugin-relative short paths (e.g. /audio/library).
  // • Paths that already start with /api/ are forwarded unchanged.
  // • Short paths (not starting with /api/) are prefixed with the plugin's
  //   own base (/api/plugins/{slug}) before the Axios call is made.
  // This prevents doubled-prefix 404s that occur when an API factory
  // passes full paths to an apiClient that already has a plugin basePath.
  // Plugin-scoped API client, event bus, capabilities and host context —
  // shared with embedded components (see runtime/plugin-runtime-context).
  // Clients are cached per plugin, so their identity is stable across
  // renders (page API adapters memoised on apiClient never re-create).
  const pluginAPI = useMemo(() => getScopedPluginApi(page?.pluginId), [page?.pluginId]);

  // Get userId from auth store for plugins that need it
  const userId = useAuthStore.getState().userId ?? 'unknown';

  // Wave 11AU.2: strict capability lookup — missing caps deny everything.
  const pluginCaps = useMemo<ResolvedCapabilities>(
    () => resolvePluginCapabilities(page?.pluginId, 'PluginPageHost'),
    [page?.pluginId],
  );

  const pluginEvents = useMemo(() => getPluginEventsApi(page?.pluginId), [page?.pluginId]);

  const hostContext = useMemo<SdkUIContext>(() => buildPluginHostContext({
    pluginId: page?.pluginId ?? '',
    userId,
    navigate,
    api: pluginAPI,
    events: pluginEvents,
  }), [page?.pluginId, userId, navigate, pluginAPI, pluginEvents]);

  // ── Extract route params from URL ───────────────────────────────────
  // Compare the matched page's path pattern (e.g. "/workspace-builder/:id/edit")
  // against the current pathname to extract named parameters.
  const routeParams = useMemo<Record<string, string>>(() => {
    if (!page || !page.path.includes(':')) return {};
    const patternParts = page.path.split('/');
    const pathParts = location.pathname.split('/');
    const params: Record<string, string> = {};
    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i].startsWith(':')) {
        const paramName = patternParts[i].slice(1);
        if (pathParts[i]) {
          params[paramName] = decodeURIComponent(pathParts[i]);
        }
      }
    }
    return params;
  }, [page?.path, location.pathname]);

  // Page not found
  if (!page) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <PageNotFound path={location.pathname} />;
  }
  
  // Error state
  if (error) {
    return <PageErrorFallback error={error} page={page} onRetry={() => setError(null)} />;
  }

  const requiredPermissions = Array.isArray(page.requiredPermissions) ? page.requiredPermissions : [];
  const hasPageAccess = requiredPermissions.length === 0
    || permissionsLoading
    || checkPermissions(requiredPermissions);

  if (!hasPageAccess) {
    return <PageAccessDenied title={page.title} />;
  }
  
  // Render based on component type
  if (page.componentUrl) {
    // Render in iframe
    return (
      <PluginIframe
        pluginId={page.pluginId}
        bundleUrl={page.componentUrl}
        className="w-full h-full"
        onError={(err) => setError(new Error(err))}
      />
    );
  }
  
  if (page.component) {
    // Render React component (lazy loaded)
    const PageComponent = page.component;

    return (
      <PageActionProvider pageId={location.pathname} activePluginId={page.pluginId}>
        <PageActionLayout>
          <PluginUIProvider
            pluginId={page.pluginId}
            version="1.0.0"
            capabilities={pluginCaps}
            hostContext={hostContext}
          >
            <Suspense fallback={<PageLoadingFallback />}>
              <ErrorBoundary
                onError={(err) => setError(err)}
                page={page}
              >
                <PageComponent
                  api={pluginAPI}
                  apiClient={pluginAPI}
                  navigate={navigate}
                  userId={userId}
                  pluginId={page.pluginId}
                  pagePath={page.path}
                  routeParams={routeParams}
                  events={pluginEvents}
                  {...routeParams}
                />
              </ErrorBoundary>
            </Suspense>
          </PluginUIProvider>
        </PageActionLayout>
      </PageActionProvider>
    );
  }
  
  // No component specified
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%',
      color: 'var(--nc-color-text-muted)',
      fontSize: 'var(--nc-size-sm)',
    }}>
      Page &ldquo;{page.title}&rdquo; has no component configured.
    </div>
  );
}

/**
 * Enhanced error boundary component — Wave A9.25
 *
 * Catches render-time errors AND listens for plugin-scoped async
 * runtime errors emitted via the `neuro:plugin-runtime-error` CustomEvent.
 * Reports all errors to the plugin telemetry pipeline.
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  page: PluginPageDefinition;
  onError?: (error: Error) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorType: 'render' | 'async' | null;
  retryCount: number;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private runtimeHandler: ((event: Event) => void) | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorType: null, retryCount: 0 };
  }
  
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorType: 'render' };
  }

  componentDidMount(): void {
    // Listen for async runtime errors scoped to this plugin
    this.runtimeHandler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail || detail.pluginId !== this.props.page.pluginId) return;

      const error = new Error(detail.error?.message ?? 'Plugin async error');
      error.stack = detail.error?.stack;

      // Don't override an existing render error with an async error
      if (this.state.hasError && this.state.errorType === 'render') return;

      this.setState({ hasError: true, error, errorType: 'async' });
      this.emitTelemetry(error, 'async-rejection', detail.operation);
    };

    window.addEventListener('neuro:plugin-runtime-error', this.runtimeHandler);
  }

  componentWillUnmount(): void {
    if (this.runtimeHandler) {
      window.removeEventListener('neuro:plugin-runtime-error', this.runtimeHandler);
      this.runtimeHandler = null;
    }
  }
  
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (reloadForChunkLoadError(error)) return;
    this.emitTelemetry(error, 'render-error', undefined, errorInfo.componentStack ?? undefined);
    this.props.onError?.(error);
  }

  private emitTelemetry(
    error: Error,
    eventType: string,
    operation?: string,
    componentStack?: string,
  ): void {
    const { page } = this.props;

    // Emit structured telemetry event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('neuro:plugin-telemetry-event', {
          detail: {
            type: 'plugin_telemetry_event',
            pluginId: page.pluginId,
            payload: {
              eventType: `runtime.${eventType}`,
              metricCategory: 'runtime-error',
              severity: 'high',
              message: error.message,
              metadata: {
                pagePath: page.path,
                pageTitle: page.title,
                operation,
                componentStack,
                retryCount: this.state.retryCount,
              },
              source: 'PluginPageHost.ErrorBoundary',
              stack: error.stack,
            },
            timestamp: Date.now(),
          },
        }),
      );
    }

    console.error(
      `[PluginPageHost] ${eventType} in plugin "${page.pluginId}" at ${page.path}:`,
      error,
    );
  }

  private handleRetry = (): void => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorType: null,
      retryCount: prev.retryCount + 1,
    }));
  };
  
  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <PageErrorFallback
          error={this.state.error}
          page={this.props.page}
          onRetry={this.state.retryCount < 3 ? this.handleRetry : undefined}
        />
      );
    }
    
    return this.props.children;
  }
}

export default PluginPageHost;
